import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Check, X, Star, StarOff, RotateCcw, Trash2 } from "lucide-react";
import AccessDenied from "../components/AccessDenied";

export default function Admin() {
    const { user, ready } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("pending");
    const [pending, setPending] = useState([]);
    const [rejected, setRejected] = useState([]);
    const [approved, setApproved] = useState([]);
    const [loading, setLoading] = useState(true);
    const [denied, setDenied] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const [p, r, a] = await Promise.all([
                api.get("/admin/applications", { params: { status: "pending" } }).then((x) => x.data),
                api.get("/admin/applications", { params: { status: "rejected" } }).then((x) => x.data),
                api.get("/admin/startups").then((x) => x.data),
            ]);
            setPending(p); setRejected(r); setApproved(a);
        } catch (e) {
            if (e?.response?.status === 403) setDenied(true);
        } finally { setLoading(false); }
    };

    useEffect(() => {
        if (!ready) return;
        if (!user) { navigate("/login?next=/admin"); return; }
        if (user.role !== "admin") { setDenied(true); setLoading(false); return; }
        refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, user]);

    const review = async (id, status) => {
        try {
            await api.post(`/admin/applications/${id}/review`, { status, note: "" });
            toast.success(status === "approved" ? "Approved" : "Rejected");
            refresh();
        } catch { toast.error("Failed"); }
    };

    const toggleFeatured = async (id, featured) => {
        try {
            await api.post(`/admin/startups/${id}/featured`, { featured });
            toast.success(featured ? "Marked as Editor's Pick" : "Removed from Editor's Pick");
            setApproved((a) => a.map((s) => s.id === id ? { ...s, featured } : s));
        } catch { toast.error("Failed"); }
    };

    const deleteStartup = async (id, name) => {
        if (!window.confirm(`Remove "${name}" from Primates? This deletes their listing, drops and follower data. This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/startups/${id}`);
            toast.success(`${name} removed`);
            setApproved((a) => a.filter((s) => s.id !== id));
        } catch { toast.error("Failed to remove"); }
    };

    if (!ready || loading) return <div className="min-h-screen flex items-center justify-center text-white/50">Loading...</div>;
    if (denied) return <AccessDenied role={user?.role} />;

    const list = tab === "pending" ? pending : tab === "rejected" ? rejected : approved;

    return (
        <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 min-h-screen">
            <p className="font-mono-tag text-xs uppercase text-brand-saffron mb-3">/ admin</p>
            <h1 className="font-display font-extrabold text-4xl md:text-6xl tracking-tighter uppercase mb-10">
                Editorial <span className="text-brand-saffron">console</span>
            </h1>

            <div className="flex gap-2 mb-10 border-b border-white/10 overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0 scrollbar-hide">
                {[
                    { id: "pending", label: `Pending (${pending.length})` },
                    { id: "rejected", label: `Rejected (${rejected.length})` },
                    { id: "approved", label: `Approved (${approved.length})` },
                ].map((t) => (
                    <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
                        className={`shrink-0 py-3 px-5 font-mono-tag text-xs uppercase transition-colors ${tab === t.id ? "text-brand-saffron border-b-2 border-brand-saffron" : "text-white/60 hover:text-white"}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {list.length === 0 ? (
                <div className="border border-dashed border-white/15 rounded-md py-20 text-center">
                    <p className="font-display text-2xl mb-2">Nothing here.</p>
                    <p className="text-white/60 text-sm">Come back when there's activity in this bucket.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {list.map((a) => (
                        <div key={a.id} data-testid={`admin-app-${a.id}`} className="grain-card p-6 rounded-md flex flex-col md:flex-row gap-6 items-start">
                            {a.logo_url && <img src={a.logo_url} alt={a.brand_name} className="w-20 h-20 rounded object-cover border border-white/10" />}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="font-display font-extrabold text-2xl tracking-tight">{a.brand_name}</h3>
                                    {a.featured && (
                                        <span className="font-mono-tag text-[10px] uppercase px-2 py-1 bg-brand-marigold text-black rounded-sm flex items-center gap-1"><Star className="w-3 h-3 fill-current" /> Editor's Pick</span>
                                    )}
                                    {a.categories?.includes("artisanal") && (
                                        <span className="font-serif-editorial italic text-[11px] px-2 py-1 bg-brand-madder text-white rounded-sm">Handcrafted</span>
                                    )}
                                </div>
                                <p className="text-white/70 text-sm mt-1">{a.tagline}</p>
                                <p className="text-white/60 text-xs mt-3 line-clamp-3">{a.description}</p>
                                <div className="mt-3 flex flex-wrap gap-2 font-mono-tag text-[10px] uppercase text-white/60">
                                    {a.city && <span>📍 {a.city}</span>}
                                    {a.founded_year && <span>· est. {a.founded_year}</span>}
                                    {a.founder_name && <span>· {a.founder_name}</span>}
                                    <a href={a.website_url} target="_blank" rel="noopener noreferrer" className="text-brand-saffron hover:underline">{a.website_url}</a>
                                </div>
                            </div>
                            <div className="flex flex-col md:flex-row gap-2 shrink-0">
                                {tab === "pending" && (
                                    <>
                                        <Button data-testid={`admin-approve-${a.id}`} onClick={() => review(a.id, "approved")}
                                            className="bg-brand-saffron text-black hover:bg-brand-saffron/90 font-semibold">
                                            <Check className="w-4 h-4 mr-1" /> Approve
                                        </Button>
                                        <Button data-testid={`admin-reject-${a.id}`} onClick={() => review(a.id, "rejected")} variant="outline"
                                            className="border-white/30 text-white bg-transparent hover:bg-brand-magenta hover:border-brand-magenta">
                                            <X className="w-4 h-4 mr-1" /> Reject
                                        </Button>
                                    </>
                                )}
                                {tab === "rejected" && (
                                    <Button data-testid={`admin-reapprove-${a.id}`} onClick={() => review(a.id, "approved")}
                                        className="bg-brand-saffron text-black hover:bg-brand-saffron/90 font-semibold">
                                        <RotateCcw className="w-4 h-4 mr-1" /> Re-approve
                                    </Button>
                                )}
                                {tab === "approved" && (
                                    <>
                                        <Button data-testid={`admin-featured-toggle-${a.id}`} onClick={() => toggleFeatured(a.id, !a.featured)}
                                            variant="outline"
                                            className={a.featured
                                                ? "bg-brand-marigold text-black border-brand-marigold hover:bg-brand-marigold/90 font-semibold"
                                                : "bg-transparent text-white border-white/30 hover:border-brand-marigold hover:text-brand-marigold"}>
                                            {a.featured ? <><StarOff className="w-4 h-4 mr-1" /> Unpick</> : <><Star className="w-4 h-4 mr-1" /> Editor's Pick</>}
                                        </Button>
                                        <Button data-testid={`admin-remove-${a.id}`} onClick={() => deleteStartup(a.id, a.brand_name)}
                                            variant="outline"
                                            className="bg-transparent text-white border-white/30 hover:bg-brand-magenta hover:border-brand-magenta hover:text-white">
                                            <Trash2 className="w-4 h-4 mr-1" /> Remove
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </main>
    );
}
